import React, { createContext, useContext, useCallback, useState } from 'react'
import { nanoid } from 'nanoid'
import type { ToastOptions, ToastInstance } from './types'

interface ToastContextValue {
  toasts: ToastInstance[]
  toast: (options: ToastOptions) => string
  dismiss: (toastId: string) => void
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined)

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [toasts, setToasts] = useState<ToastInstance[]>([])

  const dismiss = useCallback((toastId: string) => {
    setToasts((currentToasts) =>
      currentToasts.map((t) =>
        t.id === toastId ? { ...t, visible: false } : t,
      ),
    )

    // Remove toast from state after animation completes
    setTimeout(() => {
      setToasts((currentToasts) =>
        currentToasts.filter((t) => t.id !== toastId),
      )
    }, 150)
  }, [])

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = nanoid()
      const duration = options.duration ?? 5000

      const newToast: ToastInstance = {
        id,
        visible: true,
        duration,
        ...options,
      }

      setToasts((currentToasts) => [...currentToasts, newToast])

      if (duration !== Infinity) {
        setTimeout(() => {
          dismiss(id)
        }, duration)
      }

      return id
    },
    [dismiss],
  )

  return (
    <ToastContext.Provider value={{ toasts, toast, dismiss }}>
      {children}
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = useContext(ToastContext)
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider')
  }
  return context
}
